import {
  BrowserAuthError,
  ClientConfigurationError,
  InteractionRequiredAuthError,
  InteractionStatus,
  InteractionType,
} from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from '@azure/msal-react';
import { useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import 'css/bootstrap.min.css';
import 'css/viking.css';
import { MSAL_CONFIG } from 'common/AuthConfig';
import { APP_PAGES, REDIRECT_PATHS } from 'common/Constants';
import { receiveCSAUserAuthData } from 'common/UserStore/UserActions';
import AppLayout from 'components/layouts/AppLayout';
import PreAuthLayout from 'components/layouts/PreAuthLayout';
import PrivateRoute from 'components/PrivateRoute/PrivateRoute';
import SignOut from 'components/SignOut/SignOut';
import { logError } from 'utils/logger';
import PageNotFound from './pageNotFound/PageNotFound';
import SearchByUserContainer from './search/SearchByUserContainer';
import SearchContainer from './search/SearchContainer';

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const cancelTokenRef = useRef('');
  const autoTokenCountRef = useRef(0);
  const SIGN_IN_STATES = {
    NONE: 'none',
    SIGNED_IN: 'signed-in',
    SIGNED_OUT: 'signed-out',
    SIGNING_IN: 'signing-in',
    SIGNING_OUT: 'signing-out',
  };
  const [signInState, setSignInState] = useState(SIGN_IN_STATES.NONE);

  const { inProgress, instance: msalInstance } = useMsal();
  const account =
    msalInstance.getAllAccounts()?.length > 1
      ? msalInstance.getAllAccounts().find((acct) => acct.idTokenClaims?.aud === MSAL_CONFIG.auth.clientId)
      : msalInstance.getActiveAccount();

  const signOut = async () => {
    if (inProgress === InteractionStatus.None) {
      if (cancelTokenRef.current) {
        const cancelId = cancelTokenRef.current;
        clearInterval(cancelId);
      }
      setSignInState(SIGN_IN_STATES.SIGNING_OUT);
      await msalInstance.logoutRedirect({
        ...MSAL_CONFIG,
        account,
      });
      setSignInState(SIGN_IN_STATES.SIGNED_OUT);
    }
  };

  const acquireTokens = (acct) => {
    if (acct) {
      const tokenRequest = {
        authority: MSAL_CONFIG.auth.authority,
        scopes: MSAL_CONFIG.auth.scopes,
      };
      return msalInstance
        .acquireTokenSilent(tokenRequest)
        .then((res) => {
          dispatch(receiveCSAUserAuthData(res));
          return res;
        })
        .catch((error) => {
          logError({ logData: error, name: 'App.jsx - aquireTokens' });
        });
    }
    return null;
  };

  useEffect(() => {
    if (
      account?.idTokenClaims &&
      inProgress === InteractionStatus.None &&
      ![SIGN_IN_STATES.SIGNED_OUT, SIGN_IN_STATES.SIGNING_OUT].includes(signInState)
    ) {
      acquireTokens(account);
      if (cancelTokenRef.current) {
        const cancelId = cancelTokenRef.current;
        clearInterval(cancelId);
      }
      // Reset auto-refresh counter
      if (autoTokenCountRef.current) {
        const autoRefreshCount = 0;
        autoTokenCountRef.current = autoRefreshCount;
      }
      // Auto-refresh token every 15 minutes up to 30 times
      const intervalId = setInterval(
        () => {
          const autoRefreshCount = autoTokenCountRef.current + 1;
          autoTokenCountRef.current = autoRefreshCount;
          if (autoRefreshCount < 30) {
            acquireTokens(account);
          } else {
            const cancelId = cancelTokenRef.current;
            clearInterval(cancelId);
            signOut();
          }
        },
        15 * 60 * 1000,
      );
      cancelTokenRef.current = intervalId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, inProgress]);

  const { error, login } = useMsalAuthentication(
    InteractionType.Silent,
    MSAL_CONFIG,
    account !== null && account !== undefined,
  );

  useEffect(() => {
    if (!account && inProgress === InteractionStatus.None && !error) {
      setSignInState(SIGN_IN_STATES.SIGNING_IN);
      login(InteractionType.Redirect, MSAL_CONFIG);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, inProgress, error, login]);

  useEffect(() => {
    if (error && account) {
      if (
        error instanceof InteractionRequiredAuthError ||
        error instanceof BrowserAuthError ||
        error instanceof ClientConfigurationError
      ) {
        signOut();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, account]);

  const Container = account ? AppLayout : PreAuthLayout;

  const routes = (
    <Routes location={location}>
      {REDIRECT_PATHS.includes(location.pathname) && (
        <Route element={<Navigate replace to={APP_PAGES.INDEX.path} />} path={location.pathname} />
      )}
      <Route element={<SignOut signOut={signOut} />} path={APP_PAGES.SIGN_OUT.path} />
      <Route
        element={<PrivateRoute element={<SearchContainer />} requiredRoles={APP_PAGES.INDEX.roles} />}
        path={APP_PAGES.INDEX.path}
      />
      <Route
        element={<PrivateRoute element={<SearchByUserContainer />} requiredRoles={APP_PAGES.USERS.roles} />}
        path={APP_PAGES.USERS.path}
      />
      <Route element={<PageNotFound />} path="*" />
    </Routes>
  );

  useIdleTimer({
    onIdle: signOut,
    timeout: 60 * 60 * 1000,
  });

  return <Container>{routes}</Container>;
};

export default App;
